import { ClerkProvider } from '@clerk/clerk-react';
import { dark } from '@clerk/themes';
import { createRoot } from 'react-dom/client';
import constants from '@playcastdotio/GlobalConfig';
import App from './App';
import * as Sentry from '@sentry/react';
import config from './config.json';
import { initSentryRenderer } from './sentry/sentryRenderer';
import { toast } from 'sonner';

if (config.SENTRY_DSN && constants.BRANCH !== 'local') {
  initSentryRenderer({
    dsn: config.SENTRY_DSN,
    version: constants.VERSION,
    environment: constants.ENVIRONMENT,
    branch: constants.BRANCH,
  });

  Sentry.logger.info('Sentry initialized');
}

// Set initial host context tags (guest mode, cloud host token)
const isGuestMode = window.playcast?.guestAccount ?? false;
const hasCloudHostToken = !!window.playcast?.cloudHostToken;
Sentry.setTag('guest_mode', isGuestMode);
Sentry.setTag('cloud_host_token_present', hasCloudHostToken);
Sentry.setContext('host_info', {
  guest_mode: isGuestMode,
  cloud_host_token_present: hasCloudHostToken,
  steam_install: window.playcast?.steamInstall,
  version: constants.VERSION,
  branch: constants.BRANCH,
});

const container = document.getElementById('root') as HTMLElement;
const root = createRoot(container);

// Setup mutation observer to handle inert attribute cleanup
const observer = new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    if (mutation.type === 'attributes' && mutation.attributeName === 'data-floating-ui-inert') {
      const rootElement = document.getElementById('root');
      const clerkElement = document.getElementById('clerk-components');

      if (rootElement && !rootElement.hasAttribute('data-floating-ui-inert')) {
        rootElement.removeAttribute('inert');
      }

      if (clerkElement && !clerkElement.hasAttribute('data-floating-ui-inert')) {
        clerkElement.removeAttribute('inert');
      }
    }
  });
});

// Start observing the root element for attribute changes
observer.observe(container, {
  attributes: true,
  attributeFilter: ['data-floating-ui-inert'],
});

const PUBLISHABLE_KEY = constants.CLERK_PUBLIC_KEY;

// Log startup mode for debugging
console.log('[Main] Initializing host-frontend:', {
  isGuestMode,
  steamInstall: window.playcast?.steamInstall,
  version: constants.VERSION,
});

if (isGuestMode) {
  console.log('[Main] Guest mode detected - ClerkProvider present but using anonymous auth');
}

// Setup IPC listener for host close diagnostics
// This captures close events from the Electron main process and reports them to Sentry
if (typeof window !== 'undefined' && window.electron) {
  window.electron.on('host-close-diagnostic', async (data: { reason: string; stack: string; timestamp: number }) => {
    console.log('[Main] Host close diagnostic received:', data);

    if (Sentry && Sentry.captureMessage) {
      try {
        Sentry.captureMessage(`Host Electron Process Closing: ${data.reason}`, {
          level: 'warning',
          tags: {
            close_reason: data.reason,
            event_type: 'host_close',
          },
          contexts: {
            close_info: {
              reason: data.reason,
              stack_trace: data.stack,
              timestamp: data.timestamp,
              guest_mode: isGuestMode,
            },
          },
        });
        console.log('📤 Close diagnostic queued for Sentry');

        // CRITICAL: Flush Sentry to ensure event is sent before app closes
        // Sentry.captureMessage() only queues events - flush() actually sends them
        if (Sentry.flush) {
          console.log('⏳ Flushing Sentry events...');
          await Sentry.flush(2000); // Wait up to 2 seconds for events to be sent
          console.log('✅ Sentry flush complete - diagnostic sent');
        } else {
          console.warn('⚠️ Sentry.flush not available');
        }
      } catch (error) {
        console.error('❌ Failed to send/flush Sentry diagnostic:', error);
      }
    } else {
      console.warn('❌ Sentry not initialized, cannot send close diagnostic');
    }
  });
  console.log('✅ Host close diagnostic listener registered');

  // Setup IPC listener for Sentry logs from Electron main process
  // This forwards logs from main process (Node.js) to Sentry via the frontend
  window.electron.on('sentry-log', (data: {
    level: 'debug' | 'info' | 'warn' | 'error';
    message: string;
    extra?: Record<string, unknown>;
    timestamp: number;
    source: string;
  }) => {
    try {
      // Use Sentry.logger for the appropriate level
      const logMethod = Sentry.logger[data.level] || Sentry.logger.info;
      logMethod(data.message, {
        ...data.extra,
        _source: data.source,
        _timestamp: data.timestamp,
      });
    } catch (error) {
      // Fallback to console if Sentry logger fails
      console.error('[Main] Failed to forward log to Sentry:', error);
      console.log(`[${data.source}] ${data.message}`, data.extra);
    }
  });

  window.electron.on('gamebar-fallback-status', (data: {
    event: string;
    active: boolean;
    missingGameBar: boolean;
    nativeHandlerPresent: boolean;
  }) => {
    if (!data?.active || !data?.missingGameBar) {
      return;
    }

    toast.info('Game Bar is not installed. Playcast enabled a compatibility fallback to suppress Windows chooser prompts.');
    try {
      Sentry.captureMessage('[GameBarFallback] Renderer toast displayed', {
        level: 'info',
        tags: {
          gamebar_fallback_event: data.event ?? 'fallback_unchanged',
        },
        extra: {
          active: data.active,
          missingGameBar: data.missingGameBar,
          nativeHandlerPresent: data.nativeHandlerPresent,
        },
      });
    } catch {
      // no-op: diagnostics should not affect UX
    }
  });
}

root.render(
  <ClerkProvider
    publishableKey={PUBLISHABLE_KEY}
    appearance={{
      baseTheme: dark,
      elements: {
        formFieldInput: 'text-midnight-white text-sm bg-midnight-darker !py-6 rounded-lg',
        otpCodeFieldInput: 'text-gray-900 text-sm',
        socialButtonsIconButton: 'bg-midnight !border !border-midnight-bright !py-4',
        dividerLine: 'bg-midnight h-0.5',
        footer: 'no-bg !bg-midnight font-semibold',
        footerActionText: 'text-midnight-white font-semibold opacity-40',
        footerActionLink: 'text-amber-400 hover:text-amber-400 hover:opacity-60',
      },
      variables: {
        colorBackground: '#1B4366',
        colorPrimary: '#fbbf24',
        colorText: '#f2f2f2',
        colorTextOnPrimaryBackground: '#000000',
        colorTextSecondary: '#DEFFFF',
      },
    }}
    afterSignOutUrl={window.location.href}
    signInForceRedirectUrl={window.location.href}
    signUpForceRedirectUrl={window.location.href}
  >
    <App />
  </ClerkProvider>,
);
